import React, { useEffect, useRef, useState } from 'react';
import './FormField.scss';
import './MultiSelectField.scss';
import { Checkbox } from '../../features/common/CustomCheckbox/CheckBox';
import InputDropdown from './InputDropdown';

interface MultiSelectFormFieldProps {
    label?: string;
    name: string;
    value: string | number | Date | boolean | File | (string | number)[] | File[] | null;
    onChange: (name: string, value: any) => void;
    onBlur?: (name: string) => void;
    helpText?: string;
    isSelect?: boolean;
    multilineHelpText?: boolean;
    containerClassName?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    options?: { key: string; value: string }[];
    className?: string;
    placeholderOptionLabel?: string;
    isLoading?: boolean;
    isCheckboxRight?: boolean;
    readOnly?: boolean;
}

const MultiSelectFormField: React.FC<MultiSelectFormFieldProps> = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    helpText,
    multilineHelpText = false,
    containerClassName = '',
    error,
    disabled = false,
    options,
    isLoading,
    readOnly = false,
    className = '',
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const hasError = !!error;
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Fire parent onBlur only (not responsible for closing)
    const handleBlur = () => {
        if (onBlur) onBlur(name);
    };

    // Component level blur: close if focus leaves entire wrapper
    const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const nextTarget = e.relatedTarget as Node | null;
        if (nextTarget && wrapperRef.current?.contains(nextTarget)) return; // still inside
        setOpen(false);
        handleBlur();
    };



    // Multi-select helpers (store array of option keys)
    const multiSelectValues: Array<string | number> = Array.isArray(value)
        ? (value as Array<string | number>)
        : value === null || value === undefined
            ? []
            : (typeof value === 'string' || typeof value === 'number')
                ? [value]
                : [];

    const toggleMultiSelectValue = (optKey: string | number) => {
        const exists = multiSelectValues.includes(optKey);
        const next = exists ? multiSelectValues.filter((v) => v !== optKey) : [...multiSelectValues, optKey];
        onChange(name, next);
    };

    // Close on outside click or Escape
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('mousedown', handleClick);
        window.addEventListener('keydown', handleKey);
        return () => {
            window.removeEventListener('mousedown', handleClick);
            window.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    // Focus leave detection (covers elements that do not trigger blur reliably)
    useEffect(() => {
        if (!open) return;
        const handleFocusIn = () => {
            if (!wrapperRef.current) return;
            const active = document.activeElement;
            if (active && !wrapperRef.current.contains(active)) {
                setOpen(false);
            }
        };
        document.addEventListener('focusin', handleFocusIn);
        return () => document.removeEventListener('focusin', handleFocusIn);
    }, [open]);

    return (
        <div
            ref={wrapperRef}
            className={`form-field ${containerClassName}`}
            tabIndex={-1}
            onBlur={(e) => handleWrapperBlur(e)}
        >
            <div className='field-container' >
                <div className='input-wrapper'>
                    <div className='ms-root'>
                        <InputDropdown
                            value={''}
                            placeholder={label}
                            onClick={() => setOpen(!open)}
                            isOpen={open}
                            locationsStyle={className}
                        />
                        {open && (
                            <>
                                <div className='ms-dropdown'>
                                    {isLoading && <div className='ms-state'>Loading...</div>}
                                    {!isLoading && options?.length === 0 && <div className='ms-state'>No data found</div>}
                                    {!isLoading && options && options.length > 0 && (
                                        <div className='ms-options'>
                                            {options.map(opt => {
                                                const checked = multiSelectValues.includes(opt.value);
                                                return (
                                                    <label
                                                        key={opt.key}
                                                        className={`ms-option ${disabled || readOnly ? 'disabled' : ''}`}
                                                        tabIndex={0}
                                                        onClick={() => toggleMultiSelectValue(opt.value)}
                                                    >
                                                        <Checkbox isChecked={checked} Label={opt.key} onItemSelect={() => toggleMultiSelectValue(opt.value)} />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className='ms-backdrop' onClick={() => setOpen(false)} />
                            </>
                        )}
                    </div>
                </div>
            </div>
            {multilineHelpText ? (
                <span className={`help-text multiline ${hasError ? 'error' : ''}`}>
                    {error || (helpText?.split(' and ')[0] || '') + ' and '}
                    <br />
                    {!error && (helpText?.split(' and ')[1] || '')}
                </span>
            ) : (
                <span className={`help-text ${hasError ? 'error' : ''}`}>{error || helpText || ''}</span>
            )}
        </div>
    );
};

export default MultiSelectFormField;
